import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { authService } from '@/services/auth';
import { Button, Input, Card } from '@/components/UI';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    resendVerification,
    error,
    clearError,
    isLoading,
    needsSecondFactor,
    verifySecondFactor,
    resendSecondFactorCode,
  } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeResent, setCodeResent] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // A Clerk session can outlive the app's persisted store (e.g. after a
  // password reset) — if one exists, sync the store and skip the form
  useEffect(() => {
    let cancelled = false;
    authService.getSession().then((session) => {
      if (session && !cancelled) {
        useAuthStore.getState().checkAuth().then(() => {
          if (!cancelled && useAuthStore.getState().isAuthenticated) {
            navigate(from, { replace: true });
          }
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, from]);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setCodeResent(false);
    try {
      await verifySecondFactor(code);
      navigate(from, { replace: true });
    } catch {
      // message already surfaced via the store's error
    }
  };

  const handleResendCode = async () => {
    setCodeResent(false);
    try {
      await resendSecondFactorCode();
      setCodeResent(true);
    } catch {
      // surfaced via the store
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setNeedsVerification(false);
    setVerificationSent(false);
    setResendError(null);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      if (message.toLowerCase().includes('email not confirmed')) {
        setNeedsVerification(true);
        clearError();
      } else if (message.toLowerCase().includes('disabled') || message.toLowerCase().includes('deactivated')) {
        navigate('/account-disabled');
      }
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setResendError(null);

    try {
      await resendVerification(email);
      setVerificationSent(true);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center space-x-3">
          <img src="/logo.png" alt="" className="h-12 w-12" />
          <img src="/logo-text.png" alt="Statsmatic" className="h-8" />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium">
            create a new account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="py-8 px-4 sm:px-10">
          {needsSecondFactor ? (
            /* New-device verification: Clerk emailed a code and will not mint
               a session until it is entered. */
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
                We emailed a verification code to {email}. Enter it to finish
                signing in from this device.
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {codeResent && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  We sent a new code.
                </div>
              )}

              <Input
                label="Verification code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
              />

              <Button type="submit" className="w-full" isLoading={isLoading}>
                Verify
              </Button>

              <button
                type="button"
                onClick={handleResendCode}
                className="w-full text-sm text-gray-600 hover:text-gray-900"
              >
                Resend code
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {needsVerification && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
                <p className="font-medium mb-2">Please verify your email address</p>
                <p className="mb-3">Check your inbox for a verification link. If you didn't receive it, click below to resend.</p>
                {verificationSent ? (
                  <p className="text-green-700 font-medium">Verification email sent! Check your inbox.</p>
                ) : resendError ? (
                  <p className="text-red-600">{resendError}</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    className="text-primary-600 hover:text-primary-500 font-medium disabled:opacity-50"
                  >
                    {resendingVerification ? 'Sending...' : 'Resend verification email'}
                  </button>
                )}
              </div>
            )}

            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
            >
              Sign in
            </Button>
          </form>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Login;
